/* eslint-disable @khan/frontend/require-stories -- copied over from writing coach */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import type {Element, Text} from "slate";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import ToolTip from "@khanacademy/wonder-blocks-tooltip";
import {styles as wbStyles} from "@khanacademy/wonder-blocks-typography";

import warning from "@phosphor-icons/core/regular/warning.svg";

const DRAFTING_EDITABLE_PADDING_MINIMUM = spacing.xSmall_8;
const DRAFTING_EDITABLE_PADDING_FULL = spacing.xxLarge_48;
const DRAFTING_EDITABLE_PADDING_DIFFERENCE =
    DRAFTING_EDITABLE_PADDING_FULL - DRAFTING_EDITABLE_PADDING_MINIMUM;

// TODO(CL-638): Currently the borders and highlighting added here aren't
// visible to screen readers and may not meet contrast requirements.

export type CustomElement = Element & {
    paragraphNumber?: number;
    referenced?: boolean;
    paragraphWarning?: boolean;
};

type CustomText = Text & {
    referenced?: boolean;
};

// Simple placeholder to show before there is an essay
export const renderPlaceholder = (props: {
    children: React.ReactNode;
    attributes: Record<string, unknown>;
}): React.ReactElement => {
    const {children, attributes} = props;
    return (
        <span className={css(styles.placeholder)} {...attributes}>
            {children}
        </span>
    );
};

// Function for rendering a slate element, which is a paragraph.  The paragraph
// is highlighted if the focused feedback ID is in the list of feedback IDs for
// the paragraph.
export const renderElement = (props: {
    element: CustomElement;
    attributes: Record<string, unknown>;
    children: React.ReactNode;
    showParagraphWarnings: boolean;
    isDebugModeActive: boolean;
}): React.ReactElement => {
    const {
        element,
        attributes,
        children,
        isDebugModeActive,
        showParagraphWarnings,
    } = props;

    const showWarning = element.paragraphWarning && showParagraphWarnings;

    const showFocusHighlight = element.referenced && !showParagraphWarnings;

    const Element = (() => {
        switch (element.type) {
            case "heading-one":
                return StyledH1;
            case "heading-two":
                return StyledH2;
            case "heading-three":
                return StyledH3;
            case "block-quote":
                return StyledBlockquote;
            case "bulleted-list":
                return StyledUl;
            case "numbered-list":
                return StyledOl;
            case "list-item":
                return StyledLi;
            case "list-item-text":
                return StyledSpan;
            case "link":
                return StyledA;
            default:
                return StyledP;
        }
    })();

    const elementToRender = (
        <Element
            {...attributes}
            style={[
                styles.p,
                showFocusHighlight && styles.paragraphFocusHighlight,
                showWarning && styles.paragraphWarning,
            ]}
        >
            {children}
            {showWarning && (
                <PhosphorIcon
                    aria-label={t`Warning: This paragraph may contain header information`}
                    icon={warning}
                    size="small"
                    style={styles.paragraphWarningIcon}
                />
            )}
        </Element>
    );

    // If debug mode is active and the element has a paragraph number, add a
    // tooltip showing the paragraph number
    if (isDebugModeActive && element.paragraphNumber != null) {
        return (
            <ToolTip
                placement="left"
                content={`P${element.paragraphNumber}`}
                opened={true}
            >
                {elementToRender}
            </ToolTip>
        );
    }

    return elementToRender;
};

// Function for rendering a slate text node, which is a text fragment.  The
// text fragment is highlighted if the focused feedback ID is the same as the
// text fragment's feedback ID.
export const renderLeaf = (props: {
    leaf: CustomText;
    attributes: Record<string, unknown>;
    children: React.ReactNode;
    showParagraphWarnings: boolean;
}): React.ReactElement => {
    const {leaf, attributes, children, showParagraphWarnings} = props;

    return (
        <span
            {...attributes}
            className={
                leaf.referenced && !showParagraphWarnings
                    ? css(styles.leafFocusHighlight)
                    : css(
                          leaf.bold && styles.bold,
                          leaf.italic && styles.italic,
                      )
            }
        >
            {children}
        </span>
    );
};

const ICON_SIZE = 16;

const styles = StyleSheet.create({
    p: {
        marginBlockStart: 0,
        marginBlockEnd: 0,
        position: "relative",
    },
    bold: {
        fontWeight: "bold",
    },
    italic: {
        fontStyle: "italic",
    },
    leafFocusHighlight: {
        backgroundColor: "#E4EAF8",
        textDecoration: `2px underline ${semanticColor.khanmigo.primary}`,
        transition: "background-color 1s ease-in",
    },
    paragraphFocusHighlight: {
        backgroundColor: "#E4EAF8",
        borderRadius: 5,
        transition: "border-left 1s ease-in",
    },
    paragraphWarning: {
        // Matching the "warning" background color in Banner
        backgroundColor: semanticColor.core.background.warning.subtle,
        borderRadius: spacing.xxxSmall_4,
        // NOTE(@seanmcp): Aligns with the outer border of the warning banner
        marginInline: -DRAFTING_EDITABLE_PADDING_DIFFERENCE,
        paddingInline: DRAFTING_EDITABLE_PADDING_DIFFERENCE,
        // END NOTE
    },
    paragraphWarningIcon: {
        backgroundColor: semanticColor.core.background.neutral.default,
        height: ICON_SIZE,
        position: "absolute",
        insetInlineStart:
            (DRAFTING_EDITABLE_PADDING_DIFFERENCE - ICON_SIZE) / 2,
        top: (22 - ICON_SIZE) / 2,
        width: ICON_SIZE,
    },
    placeholder: {
        opacity: "1 !important", // The default style has the opacity at 0.333
        width: "auto",
        color: semanticColor.core.foreground.neutral.subtle,
    },
});

// TODO(amorelli, WB-1552): Use Wonder Blocks components in this file when they
// support `ref` forwarding.
//
// Ideally, we'd use the WB typography components (like `Body`, `Title`, and
// `HeadingLarge`) instead of these, but the WB components do not forward their
// `ref`s to the inner DOM elements, and Slate relies on DOM node `ref`s to
// work properly. The `addStyle` helper does forward `ref`s, so we use that
// here (along with the typographic styling from WB).
const StyledP = addStyle("p", wbStyles.Body); // paragraph
const StyledH1 = addStyle("h1", wbStyles.Title); // title
const StyledH2 = addStyle("h2", wbStyles.HeadingMedium); // heading
const StyledH3 = addStyle("h3", wbStyles.HeadingSmall); // subheading
const StyledBlockquote = addStyle("blockquote");
const StyledUl = addStyle("ul"); // bulleted list
const StyledOl = addStyle("ol"); // numbered list
const StyledLi = addStyle("li");
const StyledSpan = addStyle("span"); // for list item text
const StyledA = addStyle("a", wbStyles.BodyLink); // link
